/**
 * Custom API error class for structured error handling
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: any;

  constructor(message: string, status: number, code: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * Check if this is a specific type of error
   */
  is(code: string): boolean {
    return this.code === code;
  }

  /**
   * Check if this is an authentication error
   */
  isAuthError(): boolean {
    return this.status === 401 || this.code === 'AUTH_REQUIRED';
  }

  /**
   * Check if this is a validation error
   */
  isValidationError(): boolean {
    return this.status === 400 || this.code === 'VALIDATION_ERROR';
  }

  /**
   * Check if this is a not found error
   */
  isNotFoundError(): boolean {
    return this.status === 404;
  }

  /**
   * Get a user-friendly error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case 'AUTH_REQUIRED':
        return 'Please log in to continue';
      case 'OTP_NOT_FOUND':
        return 'Invalid or expired OTP code';
      case 'ALBUM_NOT_FOUND':
        return 'Album not found or access denied';
      case 'ALREADY_CLAIMED':
        return 'This album has already been claimed by another user';
      case 'CANNOT_CLAIM_OWN_ALBUM':
        return 'This album is already yours. Share the code with your client instead';
      case 'VALIDATION_ERROR':
        return 'Please check your input and try again';
      case 'UPLOAD_ERROR':
        return 'Failed to upload album. Please try again';
      case 'REDEEM_ERROR':
        return 'Failed to redeem OTP. Please check the code and try again';
      case 'DOWNLOAD_ERROR':
        return 'Failed to download file. Please try again';
      case 'NETWORK_ERROR':
        return 'Network connection error. Please check your internet connection';
      case 'TIMEOUT_ERROR':
        return 'Request timed out. Please try again';
      default:
        return this.message || 'An unexpected error occurred';
    }
  }
}

/**
 * Handle various types of errors and convert them to ApiError
 */
export function handleApiError(error: any): never {
  console.error('API Error:', error);
  
  if (error?.name === 'AbortError') {
    throw new ApiError('Request timeout', 408, 'TIMEOUT');
  }
  
  if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
    throw new ApiError('Network connection failed', 0, 'NETWORK_ERROR');
  }
  
  throw new ApiError(
    error?.message || 'Unknown error occurred',
    error?.status || 500,
    error?.code || 'UNKNOWN_ERROR'
  );
}

/**
 * Error boundary helper for React components
 */
export function getErrorMessage(error: any): string {
  if (error instanceof ApiError) {
    // Map specific error codes to user-friendly messages
    switch (error.code) {
      case 'AUTH_REQUIRED':
        return 'Please log in to continue';
      case 'ACCESS_DENIED':
        return 'You do not have permission to access this resource';
      case 'ALBUM_NOT_FOUND':
        return 'Album not found';
      case 'OTP_NOT_FOUND':
        return 'Invalid or expired code. Please check the code and try again';
      case 'OTP_EXPIRED':
        return 'This code has expired. Please request a new sharing code';
      case 'OTP_ALREADY_REDEEMED':
        return 'This code has already been used. Each code can only be used once';
      case 'ALREADY_CLAIMED':
        return 'This album has already been claimed by another user';
      case 'CANNOT_CLAIM_OWN_ALBUM':
        return 'This album is already yours. Share the code with your client instead';
      case 'VALIDATION_ERROR':
        return 'Invalid request. Please check your input and try again';
      case 'UPLOAD_ERROR':
        return 'Failed to upload album. Please try again';
      case 'REDEEM_ERROR':
        return 'Failed to redeem code. Please check the code and try again';
      case 'SHARE_ERROR':
        return 'Failed to share album. Please try again';
      case 'GET_ALBUMS_ERROR':
        return 'Failed to load albums. Please refresh the page';
      case 'GET_ALBUM_ERROR':
        return 'Failed to load album details. Please try again';
      case 'HTTP_ERROR':
        return `Network error: ${error.message}`;
      default:
        return error.message || 'An unexpected error occurred';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
} 